import React, { useState, useEffect } from 'react';
import './ProgramsAssessment.css';
import AssuranceReport from './AssuranceReport';
import { getAllPrograms, startAssessmentByProgram, deleteProgram } from '../services/api';
import { useLocation, useNavigate } from 'react-router-dom';

const ProgramsAssessment = ({ onBack, sidebarExpanded }) => {
  const location = useLocation();
  const [filter, setFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [programCourses, setProgramCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [currentSchool, setCurrentSchool] = useState('MARYLAND');
  const navigate = useNavigate();
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [programToDelete, setProgramToDelete] = useState(null);
  const [isDeletingProgram, setIsDeletingProgram] = useState(false);
  
  // Log when component mounts to verify correct onBack prop
  useEffect(() => {
    //console.log("ProgramsAssessment mounted with onBack:", !!onBack);
  }, [onBack]);
  
  // Gradient colors for cards
  const gradients = [
    'linear-gradient(135deg, #f5f7ff 0%, #e0e6ff 100%)',
    'linear-gradient(135deg, #fff5f5 0%, #ffe0e0 100%)',
    'linear-gradient(135deg, #f5fff7 0%, #e0ffe6 100%)',
    'linear-gradient(135deg, #f5f5ff 0%, #e5e0ff 100%)',
    'linear-gradient(135deg, #fffbf5 0%, #fff5e0 100%)',
    'linear-gradient(135deg, #f8f5ff 0%, #eee0ff 100%)'
  ];
  
  // Get school from URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const school = urlParams.get('school') || 'MARYLAND';
    setCurrentSchool(school);
  }, []);

  // Check if we're returning from evidence page with state
  useEffect(() => {
    if (location.state?.showAssuranceReport && location.state?.courseList) {
      //console.log("Returning from evidence page with state:", location.state);
      
      // Get school from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const school = urlParams.get('school') || 'MARYLAND';
      
      // Set the selected program name - use programName directly from state
      const programName = location.state.programName;
      //console.log(`Setting program name to: ${programName}`);
      setSelectedProgram(programName);
      
      // Set the selected course data
      setSelectedCourse({
        name: programName, // Use programName for name as well to ensure consistency
        courseList: location.state.courseList,
        school: school,
        returnedFromEvidence: true,
        fullAlignmentData: location.state.fullAlignmentData,
        originalTableData: location.state.originalTableData,
        originalData: location.state.originalData
      });
    }
  }, [location.state]);
  
  // Fetch programs when component mounts
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setLoadingPrograms(true);
        
        // Get school from URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const school = urlParams.get('school') || 'MARYLAND'; // Default to MARYLAND if not found

        const data = await getAllPrograms(school);
        
        if (data.programs && data.programs.length > 0) {
          // Add category property to each program (assuming half are Undergraduate and half are Graduate)
          const programsWithCategory = data.programs.map((program, index) => ({
            ...program,
            category: index % 2 === 0 ? 'Undergraduate' : 'Graduate'
          }));
          
          setPrograms(programsWithCategory);
        } else {
          // Fallback to dummy data if no programs returned
          setPrograms([
          ]);
        }
      } catch (err) {
        console.error('Error fetching programs:', err);
        setError('Failed to load programs');
        
        // Fallback to dummy data on error
        setPrograms([
        ]);
      } finally {
        setLoadingPrograms(false);
      }
    };
    
    fetchPrograms();
  }, []);

  const filteredPrograms = programs.filter(program => {
    const matchesFilter = filter === 'All' || program.category === filter;
    const matchesSearch = program.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const fetchProgramCourses = async (programName) => {
    setIsLoading(true);
    setError(null);
    
    // Get school from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const school = urlParams.get('school') || 'MARYLAND'; // Default to MARYLAND if not found
    
    try {
      // Use the API service to fetch program courses
      const response = await startAssessmentByProgram(programName, school);
      
      // Set the program name and directly show the AssuranceReport
      setSelectedProgram(programName);
      setSelectedCourse({
        name: programName,
        courseList: response.course_list,
        school: school
      });
    } catch (err) {
      console.error('Error fetching program courses:', err);
      setError('Failed to fetch courses. Please try again later.');
      
      // For testing/demo purposes, use some dummy data if the API fails
      setSelectedProgram(programName);
      setSelectedCourse({
        name: programName,
        courseList: ['BADM311', 'BADM312', 'BADM350'],
        school: school
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartAssessment = (program) => {
    // Get current school from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const currentSchool = urlParams.get('school') || 'MARYLAND';
    
    // Preserve the current school parameter in URL
    const url = new URL(window.location.href);
    url.searchParams.set('school', currentSchool);
    window.history.pushState({}, '', url);
    
    fetchProgramCourses(program.name);
  };

  const handleBackFromCourses = () => {
    setSelectedProgram(null);
    setProgramCourses([]);
  };
  
  const handleStartCourseAssessment = (course) => {
    setSelectedCourse(course);
  };
  
  const handleBackFromReport = () => {
    setSelectedCourse(null);
    setSelectedProgram(null);
  };
  
  // Go back to the dashboard - explicitly call onBack if provided
  const handleBackToDashboard = () => {
    // navigate to /dashboard
    navigate('/dashboard');
  };

  // Add new program handlers
  const handleAddProgramClick = () => {
    // Get school from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const school = urlParams.get('school') || 'MARYLAND';
    
    // Navigate directly to AddProgramCourses component with school parameter
    navigate(`/add-program-courses?school=${school}`);
  };

  // Delete program handler
  const handleDeleteProgram = async (programId, programName, event) => {
    // Prevent the card click event
    event.stopPropagation();
    
    // Set the program to delete and show modal
    setProgramToDelete({ id: programId, name: programName });
    setShowDeleteModal(true);
  };

  // Confirm delete from modal
  const handleConfirmDelete = async () => {
    if (!programToDelete) return;
    
    setIsDeletingProgram(true);
    
    try {
      await deleteProgram(programToDelete.id);
      
      // Remove the program from the local state
      setPrograms(prevPrograms => prevPrograms.filter(program => program.id !== programToDelete.id));
      
      //console.log(`Program "${programToDelete.name}" deleted successfully`);
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setProgramToDelete(null);
    } catch (error) {
      console.error('Error deleting program:', error);
      // Error handling will be shown in the modal
    } finally {
      setIsDeletingProgram(false);
    }
  };

  // Cancel delete from modal
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setProgramToDelete(null);
    setIsDeletingProgram(false);
  };

  // If a course is selected, show its assessment report
  if (selectedCourse) {
    return <AssuranceReport 
      onBack={handleBackFromReport} 
      programName={selectedProgram}
      courseList={selectedCourse.courseList}
      school={selectedCourse.school}
      sidebarExpanded={sidebarExpanded}
      returnedFromEvidence={selectedCourse.returnedFromEvidence}
      fullAlignmentData={selectedCourse.fullAlignmentData}
      originalTableData={selectedCourse.originalTableData}
      originalData={selectedCourse.originalData}
      initialTab={selectedCourse.returnedFromEvidence ? 'course-alignment-overview' : undefined}
    />;
  }

  // Skip the intermediate courses page entirely and just show the main programs page
  return (
    <div className="programs-assessment">
      <img src="/page-1-right-to-start.svg" alt="Programs" className="program-icon" />
      
      <div className="fixed-content">
        <div className="programs-header">
          <div className="filters">
            <div className="filter">
              <img src="/school.svg" alt="School" className="filter-icon" />
              <select className="filter-select" value={currentSchool} readOnly>
                <option value={currentSchool}>{currentSchool}</option>
              </select>
            </div>
            
            <div className="filter">
              <img src="/page-1-calendar.svg" alt="Calendar" className="filter-icon" />
              <select className="filter-select">
                <option>2025 Spring</option>
              </select>
            </div>
          </div>
          
          <button className="back-button-1" onClick={handleBackToDashboard}>
            <span className="back-icon">←</span>
            Back
          </button>
        </div>

        <div className="programs-title">
          <h1>Start Mapping By Programs</h1>
        </div>
        
        <div className="search-bar">
          <input 
            type="text" 
            placeholder="Search programs..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="program-filters">
          <button 
            className={`filter-button ${filter === 'All' ? 'active' : ''}`}
            onClick={() => setFilter('All')}
          >
            All
          </button>
          <button 
            className={`filter-button ${filter === 'Undergraduate' ? 'active' : ''}`}
            onClick={() => setFilter('Undergraduate')}
          >
            Undergraduate
          </button>
          <button 
            className={`filter-button ${filter === 'Graduate' ? 'active' : ''}`}
            onClick={() => setFilter('Graduate')}
          >
            Graduate
          </button>
        </div>
        
        {loadingPrograms && (
          <div className="loading-message">
            <p>Loading programs...</p>
          </div>
        )}
        
        {error && !loadingPrograms && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}
      </div>
      
      {!loadingPrograms && !error && (
        <div className="program-grid-container">
          {filteredPrograms.length > 0 ? (
            <div className="program-grid">
              {/* Add New Program Card */}
              <div className="program-card add-new-program-card" onClick={handleAddProgramClick}>
                <div className="program-card-content add-new-content">
                  <div className="add-icon">+</div>
                  <h3>Add a New Program</h3>
                  <button 
                    className="add-program-button"
                    onClick={handleAddProgramClick}
                  >
                    Create Program
                  </button>
                </div>
              </div>
              
              {/* Existing Program Cards */}
              {filteredPrograms.map((program, index) => (
                <div 
                  className="program-card" 
                  key={program.id}
                  style={{ 
                    background: gradients[index % gradients.length],
                    height: '249px'
                  }}
                >
                  <div className="program-card-content">
                    <div className="program-card-header">
                      <h3>{program.name}</h3>
                      <button 
                        className="delete-program-button"
                        onClick={(e) => handleDeleteProgram(program.id, program.name, e)}
                        title="Delete program"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3,6 5,6 21,6"></polyline>
                          <path d="M19,6V20a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6M8,6V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2V6"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                    <button 
                      className="start-assessment-button"
                      onClick={() => handleStartAssessment(program)}
                    >
                      Start <span className="arrow">→</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-results">
              <div className="program-grid">
                {/* Add New Program Card - Show even when no results */}
                <div className="program-card add-new-program-card" onClick={handleAddProgramClick}>
                  <div className="program-card-content add-new-content">
                    <div className="add-icon">+</div>
                    <h3>Add a New Program</h3>
                    <button 
                      className="add-program-button"
                      onClick={handleAddProgramClick}
                    >
                      Create Program
                    </button>
                  </div>
                </div>
              </div>
              
            </div>
          )}
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="delete-modal-overlay" onClick={handleCancelDelete}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <h2>Delete Program</h2>
              <button 
                className="modal-close-button" 
                onClick={handleCancelDelete}
                disabled={isDeletingProgram}
              >
                ×
              </button>
            </div>
            
            <div className="delete-modal-content">
              <div className="delete-warning-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              
              <p className="delete-message">
                Are you sure you want to delete <strong>"{programToDelete?.name}"</strong>?
              </p>
              
              <p className="delete-warning">
                This action cannot be undone. All data associated with this program will be permanently removed.
              </p>
              
              {isDeletingProgram && (
                <div className="delete-loading">
                  <div className="delete-spinner"></div>
                  <span>Deleting program...</span>
                </div>
              )}
            </div>
            
            <div className="delete-modal-footer">
              <button 
                className="delete-cancel-button" 
                onClick={handleCancelDelete}
                disabled={isDeletingProgram}
              >
                Cancel
              </button>
              <button 
                className="delete-confirm-button" 
                onClick={handleConfirmDelete}
                disabled={isDeletingProgram}
              >
                {isDeletingProgram ? 'Deleting...' : 'Delete Program'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramsAssessment; 